import React, { memo } from 'react';
import i18n from '../../../utilities/i18n';
import { FormikErrors, useFormikContext } from 'formik';
import {
  Button,
  HStack,
  IconBasalcap,
  IconButton,
  IconDiscard,
  ModalDesktop,
  Table,
  Text,
  VStack,
} from '@adc/parallax-component-library';
import PreventSubmitWrapper from '../../ui/forms/PreventSubmitWrapper';
import { StudyCreateFormValues } from '../StudyCreate';
import { Site } from '../../../models';
import CreateSiteModal from '../../modal/CreateSiteModal';
import DynamicTable, { ColumnConfig } from '../../table/DynamicTable';

const TabSites = () => {
  const { values, setFieldValue, errors } =
    useFormikContext<StudyCreateFormValues>();

  const tableColumns: ColumnConfig[] = [
    { label: i18n.t('Entity.name'), dataKey: 'name' },
    { label: i18n.t('Entity.description'), dataKey: 'description' },
    { label: i18n.t('Entity.region'), dataKey: 'region' },
    { label: '', dataKey: 'actions', isSortable: false },
  ];

  const cellRenderer = (dataKey: string, site: Site) => {
    const column = tableColumns.find((col) => col.dataKey === dataKey);
    let cell;

    if (column && column.dataKey === 'actions') {
      cell = (
        <Table.TextCell>
          <HStack gap="$2" padding="$2">
            <PreventSubmitWrapper>
              <CreateSiteModal
                studyKey=""
                site={site}
                onSuccess={(updatedSite: Site) => {
                  return setFieldValue(
                    'sites',
                    values.sites.map((s) =>
                      s.key === updatedSite.key ? updatedSite : s,
                    ),
                  ).then(
                    (errors: void | FormikErrors<StudyCreateFormValues>) => {
                      if (errors && Object.keys(errors).length > 0) {
                        return Promise.reject(errors);
                      }

                      return Promise.resolve();
                    },
                  );
                }}
                trigger={
                  <IconButton
                    variant="tertiary"
                    onPress={() => {}}
                    aria-label={i18n.t('Common.edit')}
                  >
                    <IconBasalcap />
                  </IconButton>
                }
              />
              <IconButton
                variant="tertiary"
                onPress={() => {
                  ModalDesktop.show({
                    title: i18n.t(
                      'Page_StudyCreate.tab_sites_delete_modal_title',
                    ),
                    details: i18n.t(
                      'Page_StudyCreate.tab_sites_delete_modal_message',
                    ),
                    buttons: [
                      {
                        label: i18n.t('Common.cancel'),
                        variant: 'secondary',
                        onPress: () => {
                          ModalDesktop.hide();
                        },
                      },
                      {
                        label: i18n.t('Common.delete'),
                        onPress: () => {
                          setFieldValue(
                            'sites',
                            values.sites.filter((s) => s.key !== site.key),
                          ).then(() => {
                            setFieldValue(
                              'subjects',
                              values.subjects.filter(
                                (s) => s.siteKey !== site.key,
                              ),
                            );
                            ModalDesktop.hide();
                          });
                        },
                      },
                    ],
                  });
                }}
                aria-label={i18n.t('Common.delete')}
              >
                <IconDiscard />
              </IconButton>
            </PreventSubmitWrapper>
          </HStack>
        </Table.TextCell>
      );
    } else {
      cell = (
        <Table.TextCell>{site[dataKey as keyof Site] || ''}</Table.TextCell>
      );
    }

    return cell;
  };

  return (
    <VStack
      height={'100%'}
      width={'100%'}
      gap="$2"
      backgroundColor="$neutral.0"
      padding="$2"
    >
      <Text textAlign="left">
        {i18n.t('Page_StudyCreate.tab_sites_instructions')}
      </Text>

      <HStack justifyContent="flex-end" width={'100%'}>
        <CreateSiteModal
          studyKey=""
          site={undefined}
          trigger={
            <Button
              size="medium"
              label={i18n.t('Page_StudyCreate.tab_sites_add')}
              onPress={() => {}}
            />
          }
          onSuccess={(site: Site) => {
            site.key = `${values.siteCount}`;

            return Promise.all([
              setFieldValue('siteCount', values.siteCount + 1),
              setFieldValue('sites', [...values.sites, site]).then(
                (errors: void | FormikErrors<StudyCreateFormValues>) => {
                  if (errors && Object.keys(errors).length > 0) {
                    return Promise.reject(errors);
                  }
                },
              ),
            ])
              .then(() => Promise.resolve())
              .catch((err) => Promise.reject(err));
          }}
        />
      </HStack>

      <DynamicTable
        showCard={false}
        columnData={tableColumns}
        isLoading={false}
        hasLoadingError={false}
        title=""
        description={i18n.t('Page_StudyCreate.tab_sites_table')}
        rowData={values.sites}
        cellRenderer={cellRenderer}
      />

      {(errors as any).sites && (
        <Text
          color="$support.danger.default"
          type="$labelSmall.default"
          textAlign="left"
        >
          {(errors as any).sites}
        </Text>
      )}
    </VStack>
  );
};

export default memo(TabSites);
